import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./MessageComposer.module.scss";
import {
  LocalAudio,
  LocalImage,
  LocalVideo,
  Modal,
} from "../../../../components";
import locale from "../../../../constants/locale";
import WhatsappApp from "../../../campaigns/metaWhatsappTemplates/components/WhatsappApp";
import { WhatsappFlow } from "../../../../models";
import WhatsAppPreview from "../../../campaigns/metaWhatsappFlows/components/WhatsAppPreview";

export const PreviewFiles = ({
  files,
  onRemove,
}: {
  files: Blob[];
  onRemove: () => void;
}) => {
  const { t } = useTranslation();
  const [contentTemplateSelected, setContentTemplateSelected] =
    useState<any>(undefined);
  const [waFlowSelected, setWaFlowSelected] = useState<any>(undefined);
  const [waFlowActiveScreenId, setWaFlowActiveScreenId] =
    useState<any>(undefined);

  const previews = [...files].map((file, i) => {
    switch (file.type) {
      case "image/png":
      case "image/jpeg":
        return (
          <div
            key={i}
            className={clsx("image has-rounded-corners mr-1", styles.image)}
          >
            <LocalImage file={file} alt="media" />
          </div>
        );
      case "video/mp4":
        return (
          <div
            key={i}
            className={clsx(
              "image is-16by9 has-rounded-corners mr-1",
              styles.video
            )}
          >
            <LocalVideo className="has-ratio" file={file} controls />
          </div>
        );
      case "audio/mp3":
      case "audio/mpeg":
      case "audio/ogg":
      case "audio/amr":
        return (
          <div key={i} className="mr-1">
            <LocalAudio file={file} />
          </div>
        );
      case "application/pdf":
      case "application/octet-stream":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span className="icon mr-2">
              <i className={file.type === "application/pdf" ? "fas fa-file-pdf" : "fas fa-file"}></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.ms-excel":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span className="icon mr-2">
              <i className="fas fa-file-excel"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "emailTemplate":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span className="icon mr-2">
              <i className="fa-regular fa-envelope"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "contentTemplate":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span
              className="icon mr-2"
              onClick={() => setContentTemplateSelected(file)}
            >
              <i className="fa-regular fa-eye"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "waFlow":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span
              className="icon mr-2"
              onClick={() => {
                console.log(
                  " (file as any).meta?.flow?",
                  (file as any).meta?.flow
                );
                setWaFlowActiveScreenId(
                  (file as any).meta?.flow?.flow?.screens?.[0]?.id
                );
                setWaFlowSelected(file as any);
              }}
            >
              <i className="fa-regular fa-eye"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "waResponse":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span className="icon mr-2">
              <i className="fa-regular fa-eye"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "text/vcard":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span className="icon mr-2">
              <i className="fa-regular fa-envelope"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/msword":
        return (
          <div
            key={i}
            className="is-flex has-rounded-corners has-background-light px-2 mr-1"
          >
            <span className="icon mr-2">
              <i className="fa-regular fa-file-word"></i>
            </span>
            <span className="has-ellipsis-text">{(file as File).name}</span>
          </div>
        );
      case "reply":
      case "contextualReply":
        return (
          <div className="is-flex is-flex-direction-column">
            <div className="is-size-7 has-text-grey mb-1">
              {t("Replying to")}
            </div>
            <div className="is-size-7 has-text-weight-medium">
              <span className="is-truncated">
                {(file as any)?.meta?.message || t("Reply")}
              </span>
            </div>
          </div>
        );
      default:
        return null;
    }
  });

  return (
    <div className={clsx("input is-scrollable-x", styles.input)}>
      {previews}
      <span className="delete ml-1" onClick={onRemove}></span>
      {contentTemplateSelected && (
        <Modal
          isOpen
          title={t(locale.messageTemplate) as string}
          onClose={() => setContentTemplateSelected(undefined)}
        >
          <WhatsappApp
            isMobile={true}
            contentType={contentTemplateSelected?.meta?.contentType}
            meta={contentTemplateSelected?.meta?.meta}
            message={contentTemplateSelected?.meta?.message}
          />
        </Modal>
      )}
      {waFlowSelected && waFlowActiveScreenId && (
        <Modal
          isOpen
          title={t(locale.metaWhatsappFlows) as string}
          onClose={() => setWaFlowSelected(undefined)}
        >
          <WhatsAppPreview
            isFullMode
            formData={waFlowSelected?.meta?.flow as WhatsappFlow}
            activeScreenId={waFlowActiveScreenId}
            onScreenChange={setWaFlowActiveScreenId}
          />
        </Modal>
      )}
    </div>
  );
};
